import { useCallback, useRef } from 'react';
import {
  DataImport,
  DataImportFilterInputObject,
  DataImportsQueryVariables,
  FilterGroupingEnum,
  useDataImportIdsLazyQuery,
  useDataImportsQuery,
} from 'graphql-common';
import _get from 'lodash/get';
import { GetUrlParams } from '@lib/enums/urls';
import { ApolloError } from '@apollo/client/errors';
import { FetchIdsResponse } from '@lib/utils/getAllTableItemsIds';
import FieldNames from '@lib/enums/fieldNames/importHistoryFilterFieldNames';
import { Option } from '@lib/interfaces/form';
import { ListQueryHookProps } from '../types';

export type UseDataImportsQueryHookResult = {
  collection: DataImport[];
  error: ApolloError | undefined;
  fetchIds: () => Promise<FetchIdsResponse<unknown>>;
  firstLoading: boolean;
  loading: boolean;
  refetch: () => Promise<unknown>;
  totalCount: number;
  totalPages: number;
};

export default function useDataImportsQueryHook({
  paginationParams,
  filterParams,
  orderingParams,
}: ListQueryHookProps): UseDataImportsQueryHookResult {
  const page = _get(paginationParams, GetUrlParams.Page);
  const perPage = _get(paginationParams, GetUrlParams.PerPage);
  const orderingField = _get(orderingParams, GetUrlParams.OrderingField);
  const orderingDirection = _get(
    orderingParams,
    GetUrlParams.OrderingDirection,
  );
  const queryVariables: DataImportsQueryVariables = {
    page: Number(page),
    limit: Number(perPage),
  };
  const filters: DataImportFilterInputObject[] = [];
  const typeValue = _get(filterParams, FieldNames.Type, []) as Option[];
  const createdAtValue = _get(
    filterParams,
    FieldNames.StartedAt,
    [],
  ) as string[];

  if (typeValue?.length) {
    filters.push({
      kind: {
        grouping: FilterGroupingEnum.And,
        predicate: {
          in: typeValue.map(({ value }) => value as string),
        },
      },
    });
  }
  if (createdAtValue?.[0]) {
    filters.push({
      createdAt: {
        grouping: FilterGroupingEnum.And,
        predicate: { gteq: createdAtValue[0] },
        filterGroup: 'createdAt',
      },
    });
  }
  if (createdAtValue?.[1]) {
    filters.push({
      createdAt: {
        grouping: FilterGroupingEnum.And,
        predicate: { lteq: createdAtValue[1] },
        filterGroup: 'createdAt',
      },
    });
  }
  if (filters.length) {
    queryVariables.filters = filters;
  }
  if (orderingField && orderingDirection) {
    queryVariables.sorts = {
      [orderingField]: orderingDirection,
    };
  }
  const assetsQuery = useDataImportsQuery({
    fetchPolicy: 'cache-and-network',
    variables: queryVariables,
  });
  const [fetchIdsLazy] = useDataImportIdsLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const { loading, data, previousData, refetch, error } = assetsQuery;
  const queryData = data || previousData;
  const firstLoading = loading && previousData === undefined;
  const collection = _get(queryData, 'data.collection', []) as DataImport[];
  const metadata = _get(queryData, 'data.metadata');
  const totalCount = _get(metadata, 'totalCount', 0);
  const totalPages = _get(metadata, 'totalPages', 1);

  const fetchIdsVariables = {
    onlyIds: true,
    filters: queryVariables.filters,
  };
  const fetchIdsVariablesRef = useRef(fetchIdsVariables);
  fetchIdsVariablesRef.current = fetchIdsVariables;
  // Stable ref so fetchIds is safe as a useEffect dependency (no render loop).
  const fetchIds = useCallback(
    () => fetchIdsLazy({ variables: fetchIdsVariablesRef.current }),
    [fetchIdsLazy],
  );

  return {
    fetchIds,
    refetch,
    collection,
    error,
    firstLoading,
    loading,
    totalPages,
    totalCount,
  };
}
